import { SelectField, TypeaheadField } from '@flos/react-ui';
import CoverableDetailsTemplate from '../../../../templates/CoverableDetailsTemplate';
import WizardPagetemplate from '../../../../templates/WizardPagetemplate';
import { useRecoilState, useRecoilValue } from 'recoil';
import { allCoverablesOnsaAtom } from '../../../../recoil/atoms/allCoverablesOnsaAtom';
import { getOnsaCurrentProductSelector } from '../../../../recoil/selectors/getOnsaCurrentProductSelector';
import type { CoverableInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import { useSaveCoverableInformation } from '../../../../hooks/useSaveCoverableInformation';
import { petBreedsAtom } from '../../../../recoil/atoms/petBreedsAtom';
import { useState } from 'react';
import { getCoverableIndex } from '../../../../utils/coverablesUtil';
import ViewInformationModal from '../../../../shared_components/ViewInformationModal/ViewInformationModal';
import { track } from '../../../../utils/trackingUtil';
import { useHippoText } from '../../../../hooks/useHippoText';
import HelpLink from '../../../../shared_components/HelpLink/HelpLink';

const CatBreedStep = ({
    goNext,
    goPrevious
}: {
    goNext: () => void;
    goPrevious: () => void;
}): React.JSX.Element => {
    const { saveCoverableInformation } = useSaveCoverableInformation();
    const [coverables, setCoverables] = useRecoilState(allCoverablesOnsaAtom);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const catBreeds = useRecoilValue(petBreedsAtom);
    const catBreedsNames = catBreeds.map((breed) => breed.name);
    const getHippoText = useHippoText();

    const { cats = [] } = coverables;
    const coverableIndex = getCoverableIndex(cats, currentProduct.fixedId);
    const coverable = cats[coverableIndex];

    const breed = catBreeds.find((breed) => breed.publicId === coverable.breedId)?.name;
    const { isOutdoorCat = false } = coverable;

    const [isExpand, setIsExpand] = useState<boolean>(!!breed);
    const [isValid, setIsValid] = useState<boolean>(!!breed);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleCatBreedChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { value } = event.target;
        const foundBreed = catBreeds.find((breed) => breed.name === value);

        const updatedCoverables = [...cats];

        updatedCoverables[coverableIndex] = {
            ...updatedCoverables[coverableIndex],
            breedId: foundBreed?.publicId
        };

        setCoverables((prev) => ({
            ...prev,
            cats: updatedCoverables
        }));
    };

    const handleIsOutdoorCatChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const { value } = event.target;
        const updatedCoverables = [...cats];

        updatedCoverables[coverableIndex] = {
            ...updatedCoverables[coverableIndex],
            isOutdoorCat: value === 'Det er en udekat'
        };

        setCoverables((prev) => ({
            ...prev,
            cats: updatedCoverables
        }));
    };

    const onNext = (): void => {
        setIsLoading(true);
        const updatedCoverable: CoverableInformationToUpdateDTO = {
            cat: {
                publicId: coverable.publicId,
                fixedId: coverable.fixedId,
                breedId: coverable.breedId,
                ...(isExpand ? { isOutdoorCat } : {})
            },
            productToUpdate: currentProduct.flavourName
        };

        void saveCoverableInformation(updatedCoverable).then(() => {
            isExpand ? goNext() : setIsExpand(true);

            if (isExpand) {
                track(`${coverable.flavourName}CoverableInformationBreedOfCatAndCatFunction`, [
                    { breed },
                    { isOutdoorCat }
                ]);
            }

            setIsLoading(false);
        });
    };

    return (
        <WizardPagetemplate
            onNext={onNext}
            onPrevious={goPrevious}
            disableNext={!isValid || isLoading}
            isNextLoading={isLoading}
        >
            <CoverableDetailsTemplate
                header={getHippoText('DSS-PET-COVERABLE-INFORMATION-PET-BREED-QUESTION', {
                    petname: coverable.name ?? ''
                })}
            >
                <TypeaheadField
                    label={getHippoText(
                        'DSS-PET-COVERABLE-INFORMATION-PET-BREED-QUESTION-INPUT-TOOLTIP'
                    )}
                    value={breed}
                    onChange={handleCatBreedChange}
                    onValidityChange={(isValid) => {
                        setIsValid(!!isValid);
                    }}
                    options={catBreedsNames}
                    required
                    disabled={isLoading}
                />
                <>
                    {isExpand && (
                        <>
                            <h4>
                                {getHippoText('DSS-PET-COVERABLE-INFORMATION-INDOOR-CAT-QUESTION')}
                            </h4>
                            <SelectField
                                label={getHippoText(
                                    'DSS-PET-COVERABLE-INFORMATION-INDOOR-CAT-INPUT-TOOLTIP'
                                )}
                                value={isOutdoorCat ? 'Det er en udekat' : 'Det er en indekat'}
                                onChange={handleIsOutdoorCatChange}
                                options={['Det er en indekat', 'Det er en udekat']}
                                isValid
                                required
                                disabled={isLoading}
                            />
                            <HelpLink
                                onClick={() => {
                                    setShowModal(true);
                                }}
                            >
                                {getHippoText('DSS-PET-COVERABLE-INFORMATION-CAT-HELP-ME-CHOOSE')}
                            </HelpLink>
                        </>
                    )}
                </>
            </CoverableDetailsTemplate>
            <ViewInformationModal
                closeModal={() => {
                    setShowModal(false);
                }}
                modalProps={{
                    title: 'Indekat eller udekat?',
                    description:
                        'En indekat opholder sig fast indendørs og går på intet tidspunkt frit udenfor.',
                    secondDescription:
                        'En udekat opholder og bevæger sig frit omkring både inde og ude.'
                }}
                showModal={showModal}
            />
        </WizardPagetemplate>
    );
};

export default CatBreedStep;
